import { create } from 'zustand';

import { FileUploadState } from '@/types/index';

/**
 * Represents a single file managed by the CNMC file upload store.
 */
export type CNMCFileUpload = {
  /**
   * Frontend-generated ID
   */
  id: string;
  file: File | string | null;
  name: string;
  status: FileUploadState;
  cnmcId: string;

  /**
   * Optional error message if upload fails
   */
  error?: string;
  controller?: AbortController;

  /**
   * Added to store the ID assigned by the backend
   */
  backendFileId?: string | null;
  isNewUpload?: boolean;
};

/**
 * Defines the state and actions for the CNMC file upload store.
 */
type CNMCFileUploadStore = {
  files: CNMCFileUpload[];
  /**
   * Adds a new file to the list.
   * @param fileUpload The complete CNMCFileUpload object to add.
   */
  addFile: (fileUpload: CNMCFileUpload) => void;
  /**
   * Removes a file from the list by its ID.
   * Cancellation of ongoing upload is handled by a separate hook.
   * @param id The unique ID of the file to remove.
   */
  removeFile: (id: string) => void;
  /**
   * Updates the status of a specific file.
   * @param id The unique ID of the file to update.
   * @param status The new FileUploadState for the file.
   * @param error An optional error message if the status is ERROR.
   */
  updateFileStatus: (id: string, status: FileUploadState, error?: string) => void;
  /**
   * Sets the backend-assigned file ID for a specific file.
   * @param id The unique frontend ID of the file.
   * @param backendFileId The ID assigned by the backend.
   */
  setFileBackendId: (id: string, backendFileId: string) => void; // New action
  /**
   * Clears all files from the list.
   */
  clearFiles: () => void;
};

/**
 * Zustand store for managing CNMC file uploads.
 */
export const useCNMCFileUploadStore = create<CNMCFileUploadStore>(set => ({
  files: [],
  addFile: (fileUpload: CNMCFileUpload) => {
    set(state => ({ files: [...state.files, fileUpload] }));
  },
  removeFile: (id: string) => {
    set(state => ({ files: state.files.filter(file => file.id !== id) }));
  },
  updateFileStatus: (id: string, status: FileUploadState, error?: string) => {
    set(state => {
      const updatedFiles = state.files.map(file =>
        file.id === id ? { ...file, status, error: error || file.error } : file
      );
      return { files: updatedFiles };
    });
  },
  setFileBackendId: (id: string, backendFileId: string) => {
    set(state => ({
      files: state.files.map(file => (file.id === id ? { ...file, backendFileId } : file)),
    }));
  },
  clearFiles: () => {
    set({ files: [] });
  },
}));
